import React, { useState, useEffect } from "react";
import { message, Row, Col, Typography, Table, Select } from "antd";
import GenericModal from "../../../components/buttons/genericModal";
import Button from '@mui/material/Button';
import CsvUpload from "../../../components/imports/csvUpload";
import { backendGetRequest, backendPostWithoutRefresh } from "../../../helpers/callBackend";
import LoadingButton from "@mui/lab/LoadingButton";
import { toPrice } from '../../../helpers/stringFormat';

const { Title } = Typography;

const requiredFields = ['amount', 'description', 'counterparty_name', 'transaction_type', 'balance'];
const optionalFields = ['date', 'check_number', 'reference_number'];

const csvRequirements = {
  // Primary Identifiers
  id: {
    description: 'Transaction ID',
    required: true,
    maxLength: 250,
    type: 'char'
  },
  general_ledger_account: {
    description: 'General Ledger Account ID',
    required: false,
    type: 'char'
  },

  // Boolean Flags
  is_trash: {
    description: 'Is the transaction marked as trash',
    required: false,
    type: 'boolean'
  },
  needs_review: {
    description: 'Does the transaction need review',
    required: false,
    type: 'boolean'
  },
  is_original: {
    description: 'Is the transaction original',
    required: false,
    type: 'boolean'
  },
  is_edited: {
    description: 'Is the transaction edited',
    required: false,
    type: 'boolean'
  },
  is_journal: {
    description: 'Is this a journal transaction',
    required: false,
    type: 'boolean'
  },
  is_manual: {
    description: 'Is this a manual transaction',
    required: false,
    type: 'boolean'
  },
  is_pending: {
    description: 'Is this transaction pending',
    required: false,
    type: 'boolean'
  },
  is_actively_pending: {
    description: 'Is the transaction actively pending',
    required: false,
    type: 'boolean'
  },

  // User-related Fields
  editor: {
    description: 'Editor of the transaction',
    required: false,
    maxLength: 100,
    type: 'char'
  },
  approver: {
    description: 'Approver of the transaction',
    required: false,
    maxLength: 100,
    type: 'char'
  },

  // Transaction Details
  amount: {
    description: 'Transaction amount (decimal)',
    required: true,
    type: 'decimal'
  },
  description: {
    description: 'Transaction description',
    required: true,
    maxLength: 250,
    type: 'char'
  },
  balance: {
    description: 'Account balance after transaction',
    required: true,
    type: 'decimal'
  },
  counterparty_name: {
    description: 'Name of the counterparty',
    required: true,
    maxLength: 250,
    type: 'char'
  },

  // Initialization Flags
  counterparty_name_is_initialized: {
    description: 'Is the counterparty name initialized',
    required: false,
    type: 'boolean'
  },
  category: {
    description: 'Transaction category',
    required: false,
    maxLength: 100,
    type: 'char'
  },
  category_is_initialized: {
    description: 'Is the category initialized',
    required: false,
    type: 'boolean'
  },
  transaction_type: {
    description: 'Type of transaction',
    required: true,
    maxLength: 100,
    type: 'char'
  },
  transaction_type_initialized: {
    description: 'Is the transaction type initialized',
    required: false,
    type: 'boolean'
  },

  // Additional Information
  notes: {
    description: 'Notes for the transaction',
    required: false,
    maxLength: 50,
    type: 'char'
  },
  custom_categories: {
    description: 'Custom categories for the transaction',
    required: false,
    type: 'char'
  },
  generic_tags: {
    description: 'Generic tags for the transaction',
    required: false,
    type: 'char'
  },
  check_number: {
    description: 'Check number associated with the transaction',
    required: false,
    maxLength: 20,
    type: 'char'
  },
  reference_number: {
    description: 'Reference number associated with the transaction',
    required: false,
    maxLength: 50,
    type: 'char'
  },
  date: {
    description: 'MM/DD/YYYY or MM/DD/YYYY HH:MM:SS, if time is not provided 12:00:00 AM will be assumed',
    required: false,
    maxLength: 15,
    type: 'char'
  },

  // Miscellaneous Fields
  source_response: {
    description: 'Source response in JSON format',
    required: false,
    type: 'json'
  }
};

const columns = [
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    render: (amount) => toPrice(amount)
  },
  { title: 'Description', dataIndex: 'description', key: 'description' },
  { title: 'Counterparty', dataIndex: 'counterparty_name', key: 'counterparty_name' },
  { title: 'Type', dataIndex: 'transaction_type', key: 'transaction_type' },
];

const UploadPendingTransactions = () => {
  const [bankAccounts, setBankAccounts] = useState({
    sub_portfolios: [],
    unlinked_properties: [],
  });
  const [loadingPropertyAccounts, setLoadingPropertyAccounts] = useState(true);
  const [loadingPortfolioAccounts, setLoadingPortfolioAccounts] = useState(true);
  const [selectedBankAccount, setSelectedBankAccount] = useState({});
  const [visible, setVisible] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const preview = csvData.map((obj) => ({
    amount: obj.amount,
    description: obj.description,
    counterparty_name: obj.counterparty_name,
    transaction_type: obj.transaction_type,
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        backendGetRequest(
          "/api/portfolio/bank-accounts/",
          {
            params: {
              sources: "UN",
              accounts_filter: "sub_portfolios",
              page_index: 0,
            },
          }).then((response) => {
            if (response?.data?.total_pages < 1) {
              setLoadingPortfolioAccounts(false)
            }
            for (var i = 1; i <= response?.data?.total_pages; i++) {
              backendGetRequest(
                "/api/portfolio/bank-accounts/",
                {
                  params: {
                    sources: "UN",
                    accounts_filter: "sub_portfolios",
                    page_index: i,
                  },
                }).then((response) => {
                  setBankAccounts((prevState) => ({
                    ...prevState,
                    sub_portfolios: [
                      ...prevState.sub_portfolios,
                      ...response?.data?.sub_portfolios,
                    ],
                  }));
                  setLoadingPortfolioAccounts(false);
                });
            }
          });
        backendGetRequest(
          "/api/portfolio/bank-accounts/",
          {
            params: {
              sources: "UN",
              accounts_filter: "unlinked_properties",
              page_index: 0,
            },
          }).then((response) => {
            if (response?.data?.total_pages < 1) {
              setLoadingPropertyAccounts(false)
            }
            for (var i = 1; i <= response?.data?.total_pages; i++) {
              backendGetRequest(
                "/api/portfolio/bank-accounts/",
                {
                  params: {
                    sources: "UN",
                    accounts_filter: "unlinked_properties",
                    page_index: i,
                  },
                }).then((response) => {
                  setBankAccounts((prevState) => ({
                    ...prevState,
                    unlinked_properties: [
                      ...prevState.unlinked_properties,
                      ...response?.data?.unlinked_properties,
                    ],
                  }));
                  setLoadingPropertyAccounts(false);
                });
            }
          });
      } catch (e) {
        console.error('could not fetch data', e);
      }
    }

    fetchData();
  }, []);

  const validateCSVData = () => {
    if (Object.keys(selectedBankAccount).length === 0 && selectedBankAccount.constructor === Object) {
      return message.error("Please select a bank account!");
    }

    if (csvData.length === 0) {
      return message.error("CSV File is empty. Please upload a valid file.");
    }

    for (const row of csvData) {
      for (const field of requiredFields) {
        const requirement = csvRequirements[field];

        if (requirement.required && !row[field]) {
          return message.error(`Field '${field}' is required.`);
        }

        if (row[field] && requirement.maxLength && row[field].length > requirement.maxLength) {
          return message.error(`Field '${field}' exceeds maximum length of ${requirement.maxLength}.`);
        }

        if (row[field] && requirement.type === 'decimal' && isNaN(parseFloat(row[field]))) {
          return message.error(`Field '${field}' must be a valid decimal.`);
        }

        if (row[field] && requirement.type === 'boolean' && typeof row[field] !== 'boolean') {
          return message.error(`Field '${field}' must be a boolean.`);
        }

        if (row[field] && requirement.type === 'json') {
          try {
            JSON.parse(row[field]);
          } catch (error) {
            return message.error(`Field '${field}' must be a valid JSON.`);
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = () => {
    const validation = validateCSVData();
    if (validation !== true) return;

    setSubmitting(true);
    backendPostWithoutRefresh(
      "/api/transactions/",
      {
        csv: true,
        transactions: csvData,
        bank_account_id: selectedBankAccount,
      }
    ).then((res) => {
      setSubmitting(false);
    });
  };

  return (
    <GenericModal
      size="lg"
      visible={visible}
      showModal={() => setVisible(true)}
      handleCancelModal={() => setVisible(false)}
      button={
        <Button
          disableElevation
          fullWidth
          size="small"
          variant="outlined"
          color="secondary"
          sx={{ fontSize: 14, width: '100px', textTransform: 'none' }}
        >
          Upload CSV
        </Button>
      }
      modalTitle="Upload CSV file with transactions data"
      modalBody={
        <>
          <div>
            Please select a bank account to link to the uploaded transactions.
            <Row>
              <div style={{ width: '420px', marginTop: '10px' }}>
                <Select
                  name={"Select bank account"}
                  bankAccounts={bankAccounts}
                  dropdownMatchSelectWidth={false}
                  disabled={loadingPortfolioAccounts || loadingPropertyAccounts}
                  onChange={(bankAccountArr) => setSelectedBankAccount(bankAccountArr[bankAccountArr.length - 1])}
                />
              </div>
            </Row>
            <Row>
              <div>
                <Title level={5}>Please make sure csv file contains the below fields!</Title>
                <ul style={{ display: "flex", flexWrap: "wrap", height: "135px", flexDirection: 'column', gap: "6px" }}>
                  {requiredFields.map((field) => (
                    <li key={field}><strong>{field}</strong> : {csvRequirements[field.replace('/', '_')]?.description}</li>
                  ))}
                </ul>
                <ul style={{ display: "flex", height: "100px", flexDirection: 'column', gap: "6px" }}>
                  {optionalFields.map((field) => (
                    <li key={field}><strong>{field}</strong> (Optional) : {csvRequirements[field.replace('/', '_')]?.description}</li>
                  ))}
                </ul>
              </div>
            </Row>
          </div>
          <CsvUpload setCsvData={setCsvData} />
          {csvData.length > 0 && (
            <div style={{ margin: "10px 0" }}>
              <Title level={5}>Are you sure to continue with these transactions?</Title>
              <Table
                pagination={{
                  defaultPageSize: 50,
                  showSizeChanger: true,
                }}
                columns={columns}
                dataSource={preview}
                tableLayout="fixed"
                size="small"
                rowKey={(record) => record.id || record.key} // assuming each item has a unique `id` or `key`
              />
              <LoadingButton
                onClick={handleSubmit}
                disableElevation
                fullWidth
                size="small"
                variant="contained"
                color="primary"
                loading={submitting}
                disabled={loadingPropertyAccounts || loadingPortfolioAccounts}
                loadingPosition="start"
                sx={{ fontSize: 14, width: '150px', textTransform: 'none' }}
              >
                Submit
              </LoadingButton>
            </div>
          )}
        </>
      }
    />
  );
};

export default UploadPendingTransactions;
